<script>
    import throttle from "lodash/throttle";
    import {INTERSTITIAL_MODAL_TYPES, showInterstitialModal} from "../helpers/modals";
    import {PROPERTY_SEARCH_ACTIONS} from "@milestoneslabs/milestones-property-search/src/helpers/variables";
    import LandingPageAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/LandingPageAppResource";
    import WorkspaceOrganizationMemberAppResource
        from "@milestoneslabs/milestones-core-js/src/AppResources/WorkspaceOrganizationMemberAppResource";
    import AppResourceSearch from "@milestoneslabs/milestones-js-engine/src/Data/AppResourceSearch";
    import ApiFilter from "@milestoneslabs/milestones-js-engine/src/Api/ApiFilter";
    import {HOME_PURCHASE} from "@milestoneslabs/milestones-core-js/src/AppResources/WorkspaceTypeAppResource";
    import LandingPageBuySectionAppResource
        from "@milestoneslabs/milestones-core-js/src/AppResources/LandingPageBuySectionAppResource";
    import {mapGetters} from "vuex";
    import {proxyFilter} from "@milestoneslabs/milestones-property-search/src/vue/classes/Filter";

    export default {
        name: "PropertyMapMixin",

        data() {
            return {
                projectTypeAppResource: new WorkspaceOrganizationMemberAppResource({}),
                buySection: new LandingPageBuySectionAppResource({}),
                mlsInfo: null,
                mapLocationSquare: [],
                filter: proxyFilter,
                landingPageUuid: window.landingPageUuid,
            };
        },

        props:{
            landingPage: {
                type: LandingPageAppResource,
                required: true,
            },
            isMortgageAvailable: {
                type: Boolean,
                default: false,
            },
            hasServiceProviders: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            ...mapGetters([
                'getCurrentProjectMlsId',
                'getDefaultMapCoordinates',
            ])
        },

        methods: {
            handleDisabledOption: throttle(function(action = null, params = {}) {
                let interstitialType = INTERSTITIAL_MODAL_TYPES.GET_STARTED;

                switch (action) {
                    case PROPERTY_SEARCH_ACTIONS.FAVORITE:
                        interstitialType = INTERSTITIAL_MODAL_TYPES.SAVE_FAVORITE;
                        break;
                    case PROPERTY_SEARCH_ACTIONS.SAVE_SEARCH:
                        interstitialType = INTERSTITIAL_MODAL_TYPES.SAVE_SEARCH;
                        break;
                    case PROPERTY_SEARCH_ACTIONS.ATTACH_FILES:
                        interstitialType = INTERSTITIAL_MODAL_TYPES.ATTACH_FILES;
                        break;
                    case PROPERTY_SEARCH_ACTIONS.CHAT:
                        interstitialType = INTERSTITIAL_MODAL_TYPES.CHAT;
                        break;
                    case PROPERTY_SEARCH_ACTIONS.TOUR:
                        interstitialType = INTERSTITIAL_MODAL_TYPES.TOUR;
                        break;
                    case PROPERTY_SEARCH_ACTIONS.OFFER:
                        interstitialType = INTERSTITIAL_MODAL_TYPES.OFFER;
                        break;
                    case PROPERTY_SEARCH_ACTIONS.FINANCE:
                    case PROPERTY_SEARCH_ACTIONS.TRADE:
                        return this.$router.push({ name: 'landing-page.' + action });
                }

                showInterstitialModal(
                    interstitialType,
                    this.landingPage.firstName
                        ? {
                            firstName: this.landingPage.firstName,
                            lastName: this.landingPage.lastName,
                            displayName: this.landingPage.displayName,
                            avatar: this.landingPage.avatar,
                        }
                        : {},
                    {
                        landingPageUuid: landingPageUuid,
                        type: this.projectTypeAppResource.uuid,
                        propertySearchAction: action,
                        propertyUuid: params.propertyUuid,
                    },
                );
            }, 250),

            async fetchHomePurchaseProjectType() {
                let projectTypeSearch = new AppResourceSearch('workspace-type');

                await projectTypeSearch.setSearchData({
                    filter: new ApiFilter({
                        params: {
                            type: HOME_PURCHASE,
                        },
                    }),
                });

                try {
                    await projectTypeSearch.search();

                    if (projectTypeSearch.appResourceCollection.isNotEmpty) {
                        this.projectTypeAppResource = projectTypeSearch.appResourceCollection.getFirst();
                    }
                } catch (e) {
                    console.warn(e);
                }
            },

            async fetchMls() {
                let mlsAbbreviation = this.buySection.mls || false;

                if (!mlsAbbreviation) {
                    try {
                        let mls = new AppResourceSearch('mls');

                        await mls.setSearchData({
                            filter: new ApiFilter({
                                params: {
                                    uuid: this.buySection.mlsUuid
                                }
                            })
                        }, false);

                        await mls.search();

                        this.mlsInfo = mls.appResourceCollection.getFirst() || null;
                        mlsAbbreviation = (this.mlsInfo|| {}).abbreviation || null;
                    } catch (e) {
                        console.warn(e);
                        this.showNotification({
                            message: `Something went wrong while fetching buy section! Please try again later!`,
                            type: 'error',
                        });
                    }
                } else {
                    this.mlsInfo = {
                        abbreviation: mlsAbbreviation
                    };
                }

                return mlsAbbreviation;
            },

            async fetchBuySection() {
                this.mlsInfo = null;

                this.setDefaultMapCoordinates(null);
                this.setDefaultMlsAbbreviation(null);

                if (!this.landingPageUuid) {
                    return;
                }

                try {

                    let search = new AppResourceSearch('landing-page-buy-section');
                    search.setSearchData({
                        filter: new ApiFilter({
                            params: {
                                landingPageUuid: this.landingPageUuid
                            }
                        })
                    }, false);

                    await search.search();

                    if (search.appResourceCollection.isNotEmpty) {
                        this.buySection = search.appResourceCollection.getFirst();
                        this.mapLocationSquare = this.getMapLocationSquare();

                        let abbreviation = await this.fetchMls();
                        this.setDefaultMlsAbbreviation(abbreviation);

                        let coordinates = this.getSwNeMapLocationCoord().join('|');
                        this.setDefaultMapCoordinates(coordinates);

                        if (this.getCurrentProjectMlsId) {
                            this.$bus.$emit('PropertySearchHeader:map-display', this.getSwNeMapLocationCoord());
                        }
                    }

                } catch (e) {
                    console.warn(e);
                    this.showNotification({
                        message: `Something went wrong while fetching buy section! Please try again later!`,
                        type: 'error',
                    });
                }
            },

            getSwNeMapLocationCoord() {
                let coord = [];
                if (this.mapLocationSquare.length) {
                    coord[0] = this.mapLocationSquare[0][1];
                    coord[1] = this.mapLocationSquare[0][0];
                    coord[2] = this.mapLocationSquare[2][1];
                    coord[3] = this.mapLocationSquare[2][0];
                }
                return coord;
            },

            getMapLocationSquare() {
                var points = [];
                if (((this.buySection || {}).location || {}).centroid) {
                    var distance2center = 10,
                        x = this.buySection.location.centroid.latitude,
                        y = this.buySection.location.centroid.longitude;

                    var dx = distance2center * Math.sin(Math.PI / 4);
                    var dy = distance2center * Math.cos(Math.PI / 4);

                    points[0] = [x - dx, y - dy]; // point(x1, y1)
                    points[1] = [x - dx, y + dy]; // point(x2, y2)
                    points[2] = [x + dx, y + dy]; // point(x3, y3)
                    points[3] = [x + dx, y - dy]; // point(x4, y4)
                }
                return points;
            },
        },
    };
</script>
